import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Maximize2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { fetchProducts } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const EDITORIAL_LOOKS = [
    { src: "https://customer-assets-wrfwihn1.emergentagent.net/job_wear-crafted/artifacts/72bc175069bc7ded_S1.png", productId: null, pos: "center" },
    { src: "https://customer-assets-wrfwihn1.emergentagent.net/job_wear-crafted/artifacts/931f7cd106d7abe3_image00007.webp", productId: null },
    { src: "https://customer-assets-wrfwihn1.emergentagent.net/job_wear-crafted/artifacts/b9c4f1a15b464293_image00009.webp", productId: null },
    { src: "https://customer-assets-wrfwihn1.emergentagent.net/job_wear-crafted/artifacts/b070d36672fb3d63_image00010.webp", productId: null, pos: "center" },
    { src: "https://customer-assets-wrfwihn1.emergentagent.net/job_wear-crafted/artifacts/d716eb54b64b16ac_DSC00104.webp", productId: null, pos: "center" },
    { src: "https://customer-assets-wrfwihn1.emergentagent.net/job_wear-crafted/artifacts/ed5ebba7cb6f62db_DSC00677.webp", productId: null, pos: "center" },
    { src: "https://customer-assets-wrfwihn1.emergentagent.net/job_wear-crafted/artifacts/0069198aeb53305b_DSC05350.webp", productId: null },
    { src: "https://customer-assets-wrfwihn1.emergentagent.net/job_wear-crafted/artifacts/870ef28d17a02b77_DSC05365.webp", productId: null },
    { src: "https://customer-assets-wrfwihn1.emergentagent.net/job_wear-crafted/artifacts/36f7531024332c84_DSC05375.webp", productId: null, pos: "center", full: true },
];

export default function LookbookPage() {
    const { t, lang } = useLanguage();
    const [looks, setLooks] = useState([]);
    const [lightbox, setLightbox] = useState(null);

    useEffect(() => {
        fetchProducts()
            .then((products) => {
                const found = [];
                products.forEach((p) => {
                    const images = p.images?.length ? p.images : [p.image];
                    // Le ultime due foto di ogni capo = gli scatti indossati
                    images.slice(-2).forEach((src) => {
                        if (p.id === "seva_giacca_tweed") {
                            found.push({
                                src,
                                name: lang === "en" && p.name_en ? p.name_en : p.name,
                                productId: p.id,
                            });
                        }
                    });
                });
                setLooks([
                    ...found,
                    ...EDITORIAL_LOOKS.map((e) => {
                        if (!e.productId) return { src: e.src, name: "Seva Editorial", productId: null, pos: e.pos, full: e.full };
                        const p = products.find((pr) => pr.id === e.productId);
                        return {
                            src: e.src,
                            name: p ? (lang === "en" && p.name_en ? p.name_en : p.name) : "Seva Editorial",
                            productId: e.productId,
                            pos: e.pos,
                            full: e.full,
                        };
                    }),
                ]);
            })
            .catch(() => setLooks([]));
    }, [lang]);

    return (
        <main className="pt-16 md:pt-20 min-h-screen" data-testid="lookbook-page">
            <div className="px-5 md:px-10 py-16 md:py-24">
                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    className="font-display text-5xl md:text-7xl font-extrabold uppercase tracking-tighter"
                >
                    {t("lookbook.title")}
                </motion.h1>

                {looks.length === 0 ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.7 }}
                        className="mt-24 md:mt-40 text-center"
                        data-testid="lookbook-empty"
                    >
                        <p className="font-display text-2xl md:text-4xl font-bold uppercase tracking-tight">
                            {t("lookbook.emptyTitle")}
                        </p>
                        <p className="mt-4 text-sm md:text-base text-zinc-500 max-w-md mx-auto leading-relaxed">
                            {t("lookbook.emptyBody")}
                        </p>
                        <Link
                            to="/shop"
                            data-testid="lookbook-cta-shop"
                            className="group mt-10 inline-flex items-center gap-3 bg-black text-white px-8 py-4 text-xs tracking-[0.25em] uppercase font-semibold transition-colors duration-300 hover:bg-zinc-800"
                        >
                            {t("hero.ctaShop")}
                            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                        </Link>
                    </motion.div>
                ) : (
                    <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-testid="lookbook-grid">
                        {looks.map((look, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ delay: (i % 2) * 0.12, duration: 0.7 }}
                                className="group"
                                data-testid={`lookbook-item-${i}`}
                            >
                                <button
                                    type="button"
                                    onClick={() => setLightbox(look.src)}
                                    aria-label={look.name}
                                    data-testid={`lookbook-open-${i}`}
                                    className={`block w-full overflow-hidden bg-zinc-100 cursor-zoom-in relative ${look.productId || look.full ? "" : "aspect-[3/4]"}`}
                                >
                                    <img
                                        src={look.src}
                                        alt={look.name}
                                        loading="lazy"
                                        className={
                                            look.productId || look.full
                                                ? "w-full h-auto block transition-transform duration-700 group-hover:scale-[1.02]"
                                                : `w-full h-full object-cover ${look.pos === "center" ? "object-center" : "object-top"} transition-transform duration-700 group-hover:scale-105`
                                        }
                                    />
                                    <span className="absolute bottom-3 right-3 bg-white/90 backdrop-blur p-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                                        <Maximize2 size={16} />
                                    </span>
                                </button>
                                {look.productId && (
                                    <Link
                                        to={`/prodotto/${look.productId}`}
                                        data-testid={`lookbook-link-${i}`}
                                        className="group/link mt-4 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase font-semibold"
                                    >
                                        {look.name}
                                        <ArrowRight size={14} className="transition-transform duration-300 group-hover/link:translate-x-1" />
                                    </Link>
                                )}
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>

            <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
                <DialogContent
                    className="max-w-6xl p-0 bg-black/95 border-none"
                    data-testid="lookbook-lightbox"
                >
                    {lightbox && (
                        <img
                            src={lightbox}
                            alt={t("lookbook.title")}
                            className="w-full max-h-[85vh] object-contain"
                        />
                    )}
                </DialogContent>
            </Dialog>
        </main>
    );
}
